import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { GraduationCap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const headers = () => { const t = localStorage.getItem("cauldron_token"); return t ? { Authorization: `Bearer ${t}` } : {}; };

/** Public course browse. */
export function CoursesList() {
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    axios.get(`${API}/courses`).then(r => setCourses(r.data.courses || []));
  }, []);
  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6" data-testid="courses-list">
        <div className="mb-8">
          <h1 className="text-4xl font-serif text-amber-100 mb-2">Courses</h1>
          <p className="text-amber-200/60 text-sm">Learn from verified practitioners.</p>
        </div>
        {courses.length === 0 ? (
          <div className="text-amber-200/60 py-12 text-center">No published courses yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map(c => (
              <Link key={c.id} to={`/courses/${c.id}`} data-testid={`course-card-${c.id}`}>
                <div className="rounded-lg border border-neutral-800 bg-neutral-900/40 overflow-hidden hover:border-amber-500/40 transition h-full">
                  {c.coverUrl ? (
                    <img src={c.coverUrl} alt={c.title} className="w-full h-40 object-cover" />
                  ) : (
                    <div className="w-full h-40 bg-gradient-to-br from-amber-950/60 to-neutral-900 flex items-center justify-center">
                      <GraduationCap className="h-10 w-10 text-amber-500/60" />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="text-amber-100 font-serif">{c.title}</h3>
                    <div className="text-xs text-amber-200/60 mt-1">
                      {c.provider?.name} · {c.lessonCount} lessons
                    </div>
                    <div className="text-amber-50 font-serif text-lg mt-2">${(c.priceUsd || 0).toFixed(2)}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

/** Course detail — paywalled unless enrolled. */
export function CourseDetail() {
  const { cid } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLesson, setOpenLesson] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/courses/${cid}`, { headers: headers() });
      setCourse(r.data);
    } catch { toast.error("Course not found"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [cid]);

  const enroll = async () => {
    try {
      const r = await axios.post(`${API}/courses/${cid}/enroll`, { origin: window.location.origin }, { headers: headers() });
      if (r.data.free) {
        toast.success("Enrolled!");
        load();
      } else if (r.data.url) {
        window.location.href = r.data.url;
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const complete = async (lid) => {
    try {
      await axios.post(`${API}/courses/${cid}/lessons/${lid}/complete`, {}, { headers: headers() });
      load();
    } catch { toast.error("Failed"); }
  };

  const openCertificate = async () => {
    try {
      const r = await axios.get(`${API}/courses/${cid}/certificate.html`, {
        headers: headers(),
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Certificate unavailable"); }
  };

  const [shareUrl, setShareUrl] = useState(null);
  const copyShareLink = async () => {
    try {
      const r = await axios.get(`${API}/courses/${cid}/my-certificate`, { headers: headers() });
      const url = `${window.location.origin}${r.data.publicUrl}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); toast.success("Public link copied to clipboard"); }
      catch { toast.success("Public link ready — copy from the box below"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create share link"); }
  };

  const totalLessons = (course?.lessons || []).length;
  const completedLessons = course?.enrolled
    ? Object.keys(course?.enrollment?.progress || {}).length
    : 0;
  const isFullyComplete = totalLessons > 0 && completedLessons === totalLessons;

  if (loading) return <Layout><div className="p-8 text-amber-200/60">Loading…</div></Layout>;
  if (!course) return <Layout><div className="p-8 text-amber-200/60">Course not found</div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6" data-testid="course-detail">
        <h1 className="text-4xl font-serif text-amber-100 mb-2">{course.title}</h1>
        <div className="text-sm text-amber-200/60 mb-6">
          By {course.provider?.name || "Practitioner"} · {(course.lessons || []).length} lessons · ${course.priceUsd?.toFixed(2)}
        </div>
        {course.description && <p className="text-amber-100/80 mb-6">{course.description}</p>}

        {!course.enrolled && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-950/30 p-6 mb-6 text-center">
            <div className="text-lg text-amber-100 mb-2">Enroll to access all lessons</div>
            <Button className="bg-amber-500 hover:bg-amber-400 text-neutral-900" onClick={enroll} data-testid="enroll-course">
              {course.priceUsd > 0 ? `Enroll for $${course.priceUsd.toFixed(2)}` : "Enroll (free)"}
            </Button>
          </div>
        )}

        {course.enrolled && isFullyComplete && (
          <div className="rounded-lg border border-emerald-500/40 bg-gradient-to-br from-emerald-950/40 to-neutral-900 p-6 mb-6 text-center" data-testid="certificate-earned">
            <div className="text-emerald-300 text-xs uppercase tracking-widest mb-2">✦ Course Complete</div>
            <div className="text-xl text-emerald-50 font-serif mb-3">You've earned a certificate</div>
            <div className="flex gap-2 justify-center flex-wrap">
              <Button
                onClick={openCertificate}
                className="bg-emerald-500 hover:bg-emerald-400 text-neutral-900"
                data-testid="download-certificate"
              >Open certificate</Button>
              <Button
                onClick={copyShareLink}
                variant="outline"
                className="border-emerald-500/60 text-emerald-100"
                data-testid="share-certificate"
              >Copy public link</Button>
            </div>
            {shareUrl && (
              <div className="mt-3 px-3 py-2 rounded-md border border-emerald-800/60 bg-emerald-950/40 text-xs font-mono text-emerald-100 truncate" data-testid="share-url">
                {shareUrl}
              </div>
            )}
          </div>
        )}

        <h2 className="text-2xl font-serif text-amber-100 mb-4">Lessons</h2>
        <ol className="space-y-2" data-testid="lesson-list">
          {(course.lessons || []).map((l, i) => {
            const completed = course.enrollment?.progress?.[l.id];
            return (
              <li key={l.id} className="rounded-lg border border-neutral-800 bg-neutral-900/40 overflow-hidden">
                <button
                  className="w-full flex justify-between items-center p-4 text-left hover:bg-neutral-900/70"
                  onClick={() => course.enrolled && setOpenLesson(openLesson === l.id ? null : l.id)}
                  data-testid={`lesson-${l.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-amber-500 font-serif text-sm">{i + 1}.</span>
                    <span className="text-amber-100">{l.title}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {completed && <span className="text-emerald-400 text-xs">✓ Completed</span>}
                    {!course.enrolled && <span className="text-amber-200/40 text-xs">🔒</span>}
                  </div>
                </button>
                {course.enrolled && openLesson === l.id && (
                  <div className="p-4 border-t border-neutral-800 space-y-3">
                    {l.videoUrl && <a href={l.videoUrl} target="_blank" rel="noreferrer" className="text-amber-400 underline text-sm">Watch video →</a>}
                    {l.body && <div className="text-amber-100/80 whitespace-pre-wrap text-sm">{l.body}</div>}
                    {!completed && <Button size="sm" onClick={() => complete(l.id)} data-testid={`complete-${l.id}`}>Mark complete</Button>}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </Layout>
  );
}
